// Administração — Custos Google Cloud (07/10/2026). Só o adm (user_id 2).
// Lê o espelho diário da exportação de faturamento (gcp_custo_dia): cartões do
// mês, teto por projeto (editável na linha), gráfico diário empilhado, serviços
// e modal de prévia do aviso de teto. Backend: core/views_custos_gcp.py
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { notify } from '../utils/notify';
import { formatarMoeda } from '../utils/formatters';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

const API = process.env.REACT_APP_API_BASE;
const URL_CUSTOS = `${API}/api/admin/custos-gcp`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brlSinal = (v) => `${v < 0 ? '−' : '+'}${formatarMoeda(Math.abs(v))}`;
const brl = (v) => (v == null ? '—' : formatarMoeda(v));
const pct = (v) => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);
const mesHoje = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const rotuloMes = (m) => (m ? `${MESES[Number(m.slice(5, 7)) - 1]}/${m.slice(0, 4)}` : '');

// cor = estado (tokens do painel do operador)
const COR_SITUACAO = {
  ok: 'var(--po-cyan)', atencao: 'var(--po-warn-bar)', estourado: 'var(--po-bad)', sem_teto: 'var(--po-border)',
};
const ROTULO_SITUACAO = { ok: 'Dentro do teto', atencao: 'Projeção acima do teto', estourado: 'Teto ultrapassado', sem_teto: 'Sem teto' };
// Hex espelha os tokens --po-* de painelOperador.css (navy, cyan, gold, muted, hot, ok); o Recharts grava
// a cor como atributo SVG, onde var() não é confiável.
const CORES_SERIE = ['#00205b', '#00b5cc', '#f1be48', '#5f6b7a', '#9a4a0c', '#116b47'];

const AdminCustosGcp = () => {
  const [mes, setMes] = useState(mesHoje());
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [edicao, setEdicao] = useState({});          // projeto_id -> { teto, emails }
  const [previa, setPrevia] = useState(null);        // { projeto_id, nivel, assunto, de, para, para_teste, html }
  const [enviandoTeste, setEnviandoTeste] = useState(false);

  const reqId = useRef(0);
  const mesRef = useRef(mes);
  mesRef.current = mes;

  const carregar = useCallback(async (m, { manterEdicao = false } = {}) => {
    const id = ++reqId.current;
    setCarregando(true);
    try {
      const r = await axios.get(`${URL_CUSTOS}/resumo/`, { headers: headers(), params: { mes: m } });
      if (id !== reqId.current) return;
      setDados(r.data);
      setEdicao((prev) => {
        const ed = {};
        (r.data.por_projeto || []).forEach((p) => {
          ed[p.projeto_id] = manterEdicao && prev[p.projeto_id]
            ? prev[p.projeto_id]
            : { teto: p.teto == null ? '' : String(p.teto), emails: p.emails || '' };
        });
        return ed;
      });
    } catch (e) {
      if (id !== reqId.current) return;
      setDados(null);
      notify.erro(e?.response?.status === 403 ? 'Acesso restrito ao administrador.' : 'Erro ao carregar os custos.');
    } finally {
      if (id === reqId.current) setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(mes); }, [mes, carregar]);

  // modal: Esc fecha (menos durante o envio) e trava a rolagem da página
  useEffect(() => {
    if (!previa) return undefined;
    document.body.classList.add('modal-open');
    const onKey = (ev) => { if (ev.key === 'Escape' && !enviandoTeste) setPrevia(null); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', onKey);
    };
  }, [previa, enviandoTeste]);

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await axios.post(`${URL_CUSTOS}/sincronizar/`, {}, { headers: headers() });
      notify.sucesso(`Sincronizado: ${r.data.linhas} linhas (${r.data.de} a ${r.data.ate}).`);
      await carregar(mesRef.current);
    } catch (e) {
      const erro = e?.response?.data?.erro;
      notify.erro(erro === 'exportacao_ausente' ? 'A exportação de faturamento ainda não existe no BigQuery.'
        : erro === 'sem_permissao' ? 'A conta de serviço do backend não tem permissão no dataset.'
          : `Falha no sincronismo${erro ? `: ${erro}` : ''}.`);
      await carregar(mesRef.current);
    } finally {
      setSincronizando(false);
    }
  };

  const salvarOrcamento = async (p) => {
    const ed = edicao[p.projeto_id] || {};
    if (ed.tetoInvalido) { notify.erro('Teto inválido.'); return; }
    const mesmoTeto = (ed.teto === '' && p.teto == null) || (ed.teto !== '' && p.teto != null && Number(ed.teto) === Number(p.teto));
    if (mesmoTeto && (ed.emails || '') === (p.emails || '')) return;
    try {
      const r = await axios.post(`${URL_CUSTOS}/orcamento/`, { projeto_id: p.projeto_id, teto_mes: ed.teto === '' ? null : ed.teto, emails: ed.emails, ativo: p.ativo !== 0 }, { headers: headers() });
      notify.sucesso(`Teto de ${p.nome} salvo.`);
      setEdicao((prev) => {
        const cur = prev[p.projeto_id] || ed;
        return {
          ...prev,
          [p.projeto_id]: {
            ...cur,
            teto: cur.teto === ed.teto ? (r.data.teto_mes == null ? '' : String(r.data.teto_mes)) : cur.teto,
            emails: cur.emails === ed.emails ? (r.data.emails ?? ed.emails) : cur.emails,
          },
        };
      });
      await carregar(mesRef.current, { manterEdicao: true });
    } catch (e) {
      notify.erro(e?.response?.data?.erro || 'Erro ao salvar o teto.');
    }
  };

  const abrirPrevia = async (p) => {
    const nivel = p.situacao === 'estourado' ? 'realizado' : 'projecao';
    try {
      const r = await axios.get(`${URL_CUSTOS}/aviso/previa/`, { headers: headers(), params: { projeto_id: p.projeto_id, nivel, mes } });
      setPrevia({ projeto_id: p.projeto_id, nivel, ...r.data });
    } catch (e) {
      notify.erro(e?.response?.data?.erro || 'Erro ao montar a prévia.');
    }
  };

  const enviarTeste = async () => {
    if (!previa) return;
    setEnviandoTeste(true);
    try {
      const r = await axios.post(`${URL_CUSTOS}/aviso/teste/`, { projeto_id: previa.projeto_id, nivel: previa.nivel, mes }, { headers: headers() });
      notify.sucesso(`Teste enviado para ${r.data.para}.`);
      setPrevia(null);
    } catch (e) {
      notify.erro(e?.response?.data?.erro || 'Falha no envio do teste.');
    } finally {
      setEnviandoTeste(false);
    }
  };

  const total = dados?.total || {};
  const nomes = {};
  (dados?.por_projeto || []).forEach((p) => { nomes[p.projeto_id] = p.nome; });
  const us = dados?.ultimo_sync;
  const semDado = dados && dados.exportacao_ok && !dados.serie?.length && !(us?.linhas > 0);

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-cloud header-icon me-2" aria-hidden="true"></i>
          <h2 className="titulo-pagina m-0">Custos Google Cloud</h2></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 130 }} value={mes} onChange={(e) => setMes(e.target.value)} disabled={sincronizando} aria-label="Mês">
            {(dados?.meses || [mes]).map((m) => <option key={m} value={m}>{rotuloMes(m)}</option>)}
          </select>
          <span className="small text-muted">
            {dados?.ultimo_sync ? `Última sincronização: ${dados.ultimo_sync.concluido_em || dados.ultimo_sync.iniciado_em}${dados.ultimo_sync.erro ? ` (erro: ${dados.ultimo_sync.erro})` : ''}` : 'Nunca sincronizado'}
          </span>
          <button type="button" className="gr-btn primaria" onClick={sincronizar} disabled={sincronizando || carregando}>
            <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>{sincronizando ? 'Sincronizando…' : 'Atualizar agora'}</button>
        </div>
      </div>

      {carregando && !dados && <div className="text-muted small py-4 text-center">Carregando…</div>}

      {dados && !dados.exportacao_ok && (
        <div className="alert alert-warning small">
          <div className="fw-bold mb-1"><i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i>A exportação de faturamento ainda não está disponível.</div>
          <ol className="mb-0 ps-3">
            <li>Ligar a exportação "Custo de uso padrão" da conta de faturamento para o dataset <code>faturamento</code> do projeto saber-comece.</li>
            <li>Dar à conta de serviço do backend os papéis BigQuery Job User (projeto) e BigQuery Data Viewer (dataset).</li>
            <li>Clicar em Atualizar agora.</li>
          </ol>
        </div>)}

      {dados && dados.exportacao_ok && !us && (
        <div className="alert alert-light border small"><i className="fas fa-circle-info me-2 text-primary" aria-hidden="true"></i>
          Nenhuma sincronização ainda. Clique em Atualizar agora.</div>)}

      {dados && dados.exportacao_ok && us?.erro && (
        <div className="alert alert-danger small"><i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i>
          A última sincronização falhou ({us.erro}) em {us.concluido_em || us.iniciado_em}.</div>)}

      {semDado && us && !us.erro && (
        <div className="alert alert-light border small"><i className="fas fa-circle-info me-2 text-primary" aria-hidden="true"></i>
          Aguardando o primeiro dia de dados da exportação. O Google leva até um dia para publicar o custo.</div>)}

      {dados && dados.exportacao_ok && (<div aria-busy={carregando} style={carregando ? { opacity: 0.6 } : undefined}>
        <div className="row g-3 mb-3">
          {[
            [`${rotuloMes(dados.mes)} (líquido)`, brl(total.realizado), 'fa-sack-dollar', undefined, null],
            ['Mês anterior (fechado)', brl(total.mes_anterior), 'fa-calendar-check', undefined, null],
            [dados.mes_fechado ? 'Fechamento' : 'Projeção do mês', brl(total.projecao), 'fa-chart-line', undefined, null],
            ['Variação sobre o mês anterior', pct(total.variacao_pct), 'fa-arrow-trend-up', total.variacao_pct > 0 ? 'var(--po-bad)' : total.variacao_pct < 0 ? 'var(--po-ok)' : undefined,
              total.projecao != null && total.mes_anterior != null ? brlSinal(total.projecao - total.mes_anterior) : null],
          ].map(([rot, val, ic, cor, extra]) => (
            <div className="col-6 col-lg-3" key={rot}>
              <section className="po-card po-enter h-100"><div className="po-card-body p-3">
                <div className="po-sub"><i className={`fas ${ic} me-1`} aria-hidden="true"></i>{rot}</div>
                <div className="fs-4 fw-bold" style={{ color: cor, fontVariantNumeric: 'tabular-nums' }}>{val}</div>
                {extra && <div className="small" style={{ color: cor, fontVariantNumeric: 'tabular-nums' }}>{extra}</div>}
              </div></section></div>))}
        </div>
        <div className="small text-muted mb-3">
          Bruto {brl(total.bruto)} · créditos {brl(total.creditos)} · {dados.dias_com_dado} de {dados.dias_do_mes} dias com dado.
          {!dados.mes_fechado && dados.dias_com_dado < 3 && ' A projeção aparece a partir de 3 dias com dado.'}
        </div>

        <section className="po-card po-enter mb-3"><header className="po-card-head">
          <h3><i className="fas fa-diagram-project" aria-hidden="true"></i>Por projeto</h3>
          <span className="po-sub">teto e e-mails salvam ao sair do campo</span></header>
          <div className="po-card-body"><div className="po-scroll"><table className="table table-hover po-table align-middle mb-0" style={{ fontSize: '.86rem' }}>
            <thead><tr><th style={{ minWidth: 180 }}>Projeto</th><th className="text-end">Realizado</th><th className="text-end">Projeção</th><th style={{ width: 140 }}>Teto (R$)</th><th style={{ minWidth: 200 }}>Consumo do teto</th><th style={{ minWidth: 220 }}>E-mails de aviso</th><th></th></tr></thead>
            <tbody>{(dados.por_projeto || []).map((p) => {
              const ed = edicao[p.projeto_id] || { teto: '', emails: '' };
              const largura = p.pct == null ? 0 : Math.max(0, Math.min(100, p.pct));
              return (
                <tr key={p.projeto_id}>
                  <td><div className="fw-semibold">{p.nome}</div><div className="small text-muted">{p.projeto_id}</div></td>
                  <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(p.realizado)}</td>
                  <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(p.projecao)}</td>
                  <td><input type="number" min="0" step="0.01" className="form-control form-control-sm text-end" value={ed.teto}
                    placeholder="sem teto" aria-label={`Teto de ${p.nome}`}
                    onChange={(e) => { const v = e.target.value; const bad = !!e.target.validity?.badInput; setEdicao((prev) => ({ ...prev, [p.projeto_id]: { ...(prev[p.projeto_id] || ed), teto: v, tetoInvalido: bad } })); }}
                    onBlur={() => salvarOrcamento(p)} /></td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: 1, height: 8, background: 'var(--po-surface-2)', borderRadius: 4, overflow: 'hidden' }} title={ROTULO_SITUACAO[p.situacao]}>
                        <div style={{ width: `${largura}%`, height: '100%', background: COR_SITUACAO[p.situacao], transition: 'width .3s' }} />
                      </div>
                      <span className="small" style={{ minWidth: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pct(p.pct)}</span>
                    </div>
                    <div className="small" style={{ color: p.situacao === 'estourado' ? 'var(--po-bad)' : p.situacao === 'atencao' ? 'var(--po-warn)' : 'var(--po-muted)' }}>{ROTULO_SITUACAO[p.situacao]}</div>
                  </td>
                  <td><input type="text" className="form-control form-control-sm" value={ed.emails} placeholder="padrão: administrador"
                    aria-label={`E-mails de aviso de ${p.nome}`}
                    onChange={(e) => { const v = e.target.value; setEdicao((prev) => ({ ...prev, [p.projeto_id]: { ...(prev[p.projeto_id] || ed), emails: v } })); }}
                    onBlur={() => salvarOrcamento(p)} /></td>
                  <td className="text-end"><button type="button" className="gr-btn" onClick={() => abrirPrevia(p)} disabled={p.teto == null} title={p.teto == null ? 'Defina um teto para testar o aviso' : 'Prévia do e-mail de aviso'}>
                    <i className="fas fa-envelope-open-text" aria-hidden="true"></i>Testar aviso</button></td>
                </tr>);
            })}</tbody>
          </table></div></div>
        </section>

        <div className="row g-3">
          <div className="col-lg-7">
            <section className="po-card po-enter h-100"><header className="po-card-head">
              <h3><i className="fas fa-chart-column" aria-hidden="true"></i>Custo por dia</h3>
              <span className="po-sub">{rotuloMes(dados.mes)} · líquido, empilhado por projeto</span></header>
              <div className="po-card-body p-3">
                {dados.serie?.length ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dados.serie} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e3e8ef" vertical={false} />
                      <XAxis dataKey="dia" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `R$ ${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`} width={80} />
                      <Tooltip formatter={(v, nome) => [brl(v), nome]} labelFormatter={(d) => `Dia ${d}`} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {(dados.projetos_serie || []).map((pid, i) => (
                        <Bar key={pid} dataKey={pid} name={nomes[pid] || pid} stackId="a" fill={CORES_SERIE[i % CORES_SERIE.length]} />))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="text-muted small py-4 text-center">Sem dado neste mês.</div>}
              </div>
            </section>
          </div>
          <div className="col-lg-5">
            <section className="po-card po-enter h-100"><header className="po-card-head">
              <h3><i className="fas fa-list" aria-hidden="true"></i>Por serviço</h3>
              <span className="po-sub">variação contra o mesmo período do mês anterior</span></header>
              <div className="po-card-body"><div className="po-scroll"><table className="table table-hover po-table align-middle mb-0" style={{ fontSize: '.86rem' }}>
                <thead><tr><th>Serviço</th><th className="text-end">Realizado</th><th className="text-end">% do total</th><th className="text-end">Variação</th></tr></thead>
                <tbody>{(dados.por_servico || []).map((s) => (
                  <tr key={s.servico}>
                    <td>{s.servico}</td>
                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(s.realizado)}</td>
                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{pct(s.pct_total)}</td>
                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums', color: s.variacao_pct > 0 ? 'var(--po-bad)' : s.variacao_pct < 0 ? 'var(--po-ok)' : undefined }}>{pct(s.variacao_pct)}</td>
                  </tr>))}
                {!dados.por_servico?.length && <tr><td colSpan={4} className="text-muted small text-center py-3">Sem dado neste mês.</td></tr>}
                </tbody>
              </table></div></div>
            </section>
          </div>
        </div>
      </div>)}

      {previa && (
        <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,.5)' }} tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="custos-previa-titulo">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"><div className="modal-content">
            <div className="modal-header" style={{ background: '#fff' }}>
              <h5 className="modal-title" id="custos-previa-titulo" style={{ color: '#212529' }}><i className="fas fa-envelope-open-text me-2" aria-hidden="true"></i>Prévia do aviso
                <div className="small fw-normal" style={{ color: '#6c757d' }}>{previa.nivel === 'realizado' ? 'Teto ultrapassado' : 'Projeção acima do teto'}</div></h5>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setPrevia(null)}></button>
            </div>
            <div className="modal-body">
              <div className="small mb-2"><b>Assunto:</b> {previa.assunto}</div>
              <div className="small mb-2"><b>De:</b> {previa.de}</div>
              <div className="small mb-2"><b>Para (no aviso real):</b> {(previa.para || []).join(', ')}</div>
              <div className="small mb-3"><b>Para (neste teste):</b> {(previa.para_teste || []).join(', ')}</div>
              <iframe title="Prévia do e-mail" srcDoc={previa.html} sandbox="" style={{ width: '100%', height: 520, border: '1px solid var(--po-border)', borderRadius: 8, background: '#f4f6fa' }} />
            </div>
            <div className="modal-footer bg-light">
              <button type="button" className="gr-btn" autoFocus onClick={() => setPrevia(null)}>Fechar</button>
              <button type="button" className="gr-btn primaria" onClick={enviarTeste} disabled={enviandoTeste}>
                <i className={`fas ${enviandoTeste ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} aria-hidden="true"></i>{enviandoTeste ? 'Enviando…' : 'Enviar teste'}</button>
            </div>
          </div></div>
        </div>)}
    </div></div>
  );
};

export default AdminCustosGcp;
